import { useState, type FormEvent } from 'react';
import { EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react';
// demoAdminLogin: het testaccount, alleen zolang er geen backend is.
import { adminLogin, demoAdminLogin, demoColleagueLogin, demoMarketingLogin } from './adminApi';
import { btnPrimary, inputCls } from './ui/styles';
import { errorMessage } from '../core/errors';
import { AuthShell } from './security/AuthShell';
import { DemoHintBox } from './security/DemoHintBox';
import { TwoFactorChallenge } from './security/TwoFactorChallenge';
import type { AdminUser } from '../core/types/adminRequests';
type Props = {
  onLogin: (user: AdminUser) => void;
};

/**
 * Inloggen op het beheer. Staat tweestapsverificatie aan, dan volgt na het wachtwoord
 * de code uit de app (de server geeft dan nog geen sessie). Met een demo-backend staat het
 * testaccount eronder.
 */
export function Login({
  onLogin
}: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsCode, setNeedsCode] = useState(false);
  // Lokale kopie: een geïmporteerde binding blijft in een callback niet vernauwd tot "niet null".
  const demo = demoAdminLogin;
  const others = [{
    who: 'Collega',
    sr: 'de collega',
    login: demoColleagueLogin
  }, {
    who: 'Marketing',
    sr: 'marketing',
    login: demoMarketingLogin
  }].flatMap(o => o.login ? [{
    ...o,
    login: o.login
  }] : []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await adminLogin({
        email: email.trim(),
        password
      });
      if (res.status === 'ok') return onLogin(res.user);
      setPassword('');
      setNeedsCode(true);
      setBusy(false);
    } catch (err) {
      setError(errorMessage(err) ?? 'Inloggen lukte niet. Probeer het zo nog eens.');
      setBusy(false);
    }
  };
  if (needsCode) {
    return <AuthShell data-id="element-2651" data-mp="3h0s0nkqekikm">
        <TwoFactorChallenge onDone={onLogin} onBack={() => setNeedsCode(false)} data-id="element-2652" data-mp="3h1bdin174grs" />
      </AuthShell>;
  }
  return <AuthShell data-id="element-2653" data-mp="3c0np894cqe19">
      <h1 className="mt-10 font-display text-[2.2rem] font-light leading-tight text-ink" data-id="element-2654" data-mp="373ne3jsnal3a">Welkom terug</h1>
      <p className="mt-2 text-[14px] text-muted" data-id="element-2655" data-mp="324sx8ne8n6lt">Log in om je webshop en website te beheren.</p>

      <form onSubmit={submit} className="mt-8 space-y-4" noValidate aria-describedby={error ? 'login-error' : undefined} data-id="element-2656" data-mp="2y2p9p0eaobk6">
        <label className="block" data-id="element-2657" data-mp="2tbz1zgiebwmb">
          <span className="text-[13px] font-medium text-ink" data-id="element-2658" data-mp="2taxrpe97fe5n">E-mail</span>
          <input type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} aria-invalid={Boolean(error)} className={`${inputCls} mt-1.5 py-2.5`} required data-id="element-2659" data-mp="2oe7cet584mla" />
        </label>
        <label className="block" data-id="element-2660" data-mp="2aefvoknbos3l">
          <span className="text-[13px] font-medium text-ink" data-id="element-2661" data-mp="2afhfbdiemmwx">Wachtwoord</span>
          <span className="relative mt-1.5 block" data-id="element-2662" data-mp="pusqyj8im49y">
            <input type={show ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} aria-invalid={Boolean(error)} className={`${inputCls} py-2.5 pr-10`} required data-id="element-2663" data-mp="twntfr26mnpb" />
            <button type="button" onClick={() => setShow(s => !s)} aria-label={show ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'} aria-pressed={show} className="absolute inset-y-0 right-0 px-3 text-muted hover:text-ink" data-id="element-2664" data-mp="68o0qllb8n83">
              {show ? <EyeOffIcon className="h-4 w-4" aria-hidden="true" data-id="element-2665" data-mp="kxk80gfjlw7n" /> : <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-2666" data-mp="3jmrd7d96x3bt" />}
            </button>
          </span>
        </label>

        {error && <p id="login-error" role="alert" className="rounded-md bg-blush/60 px-3 py-2 text-[13px] text-ink" data-id="element-2667" data-mp="3ojrivn24o42e">
            {error}
          </p>}

        <button type="submit" disabled={busy} className={`${btnPrimary} w-full py-3`} data-id="element-2668" data-mp="1q428qlaqdy6m">
          <LockIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2669" data-mp="23unk7oxuv5uh" />
          {busy ? 'Bezig met inloggen…' : 'Inloggen'}
        </button>
      </form>

      {demo && <DemoHintBox title="Testaccounts" data-id="element-2670" data-mp="1z2dwmfk7qhvd">
          <span className="block" data-id="element-2671" data-mp="2ds1z3k44rmko">
            Sarah: {demo.email} · wachtwoord <span className="text-ink" data-id="element-2672" data-mp="28ua8ctf826i2">{demo.password}</span>
            <button type="button" onClick={() => {
          setEmail(demo.email);
          setPassword(demo.password);
        }} className="ml-2 text-clay underline underline-offset-2" data-id="element-2673" data-mp="28tqq1blntg3i">
              Invullen<span className="sr-only" data-id="element-2674" data-mp="sgtikczsc0ya"> met het testaccount van Sarah</span>
            </button>
          </span>
          {others.map(({
        who,
        sr,
        login
      }) => <span key={who} className="mt-1 block" data-id="element-2675" data-mp="2z40u3go96o6b">
              {who}: {login.email} · wachtwoord <span className="text-ink" data-id="element-2676" data-mp="2z6dcfuxxb0jw">{login.password}</span>
              <button type="button" onClick={() => {
          setEmail(login.email);
          setPassword(login.password);
        }} className="ml-2 text-clay underline underline-offset-2" data-id="element-2677" data-mp="342lxfoysxnyz">
                Invullen<span className="sr-only" data-id="element-2678" data-mp="2gchxbrkto6cq"> met het testaccount van {sr}</span>
              </button>
            </span>)}
        </DemoHintBox>}
    </AuthShell>;
}